import { getPatientSummaryFullName } from '@/api/mappers';
import type { SortDirection } from '@/components/ui/data-table';
import type { PatientSummaryViewModel } from '@/models/patient';

/**
 * Columns the Browse Patients table can sort by. Kept as a closed union so the
 * URL param (`?sort=`), the header wiring, and the comparator can't drift.
 */
export type PatientSortKey =
  | 'name'
  | 'dob'
  | 'sex'
  | 'program'
  | 'gc'
  | 'lastModified';

const SORT_KEYS: readonly PatientSortKey[] = [
  'name',
  'dob',
  'sex',
  'program',
  'gc',
  'lastModified',
];

/** Narrow an untrusted string (e.g. from the URL) to a valid sort key. */
export function parsePatientSortKey(value: string | null): PatientSortKey | null {
  return SORT_KEYS.includes(value as PatientSortKey) ? (value as PatientSortKey) : null;
}

/** Narrow an untrusted string (e.g. from the URL) to a sort direction. */
export function parseSortDirection(value: string | null): SortDirection | null {
  return value === 'asc' || value === 'desc' ? value : null;
}

/**
 * The comparable value for a summary under a given key. `null` means "no
 * value" and always sorts to the end regardless of direction, so empty rows
 * don't crowd the top of a descending sort.
 */
function comparableValue(
  patient: PatientSummaryViewModel,
  key: PatientSortKey,
): string | null {
  switch (key) {
    case 'name':
      return getPatientSummaryFullName(patient);
    case 'dob':
      // Partial ISO (`YYYY[-MM[-DD]]`) — lexical order matches chronological.
      return patient.dateOfBirth;
    case 'sex':
      return patient.sex;
    case 'program':
      return patient.program;
    case 'gc':
      return patient.assignedGc;
    case 'lastModified':
      // ISO `date-time` — lexical order matches chronological.
      return patient.lastInteractedAt;
  }
}

/**
 * Returns a new array sorted by `key`/`direction`. When `key` is `null` the
 * original order is preserved (the list is still copied so callers can treat
 * the result as owned). Pure — no mutation of the input.
 */
export function sortPatientSummaries(
  patients: readonly PatientSummaryViewModel[],
  key: PatientSortKey | null,
  direction: SortDirection,
): readonly PatientSummaryViewModel[] {
  if (key === null) return patients.slice();
  const factor = direction === 'asc' ? 1 : -1;
  return patients.slice().sort((a, b) => {
    const av = comparableValue(a, key);
    const bv = comparableValue(b, key);
    // Empties always last, irrespective of direction. Written as direct value
    // checks (not helper booleans) so TS narrows `av`/`bv` to `string` below.
    if (av === null || av.length === 0) {
      return bv === null || bv.length === 0 ? 0 : 1;
    }
    if (bv === null || bv.length === 0) return -1;
    if (key === 'dob' || key === 'lastModified') {
      // Plain lexical compare for ISO-shaped strings.
      return av < bv ? -factor : av > bv ? factor : 0;
    }
    return av.localeCompare(bv, undefined, { sensitivity: 'base' }) * factor;
  });
}

/**
 * Cycles a column's sort state: click an inactive column → ascending;
 * ascending → descending; descending → cleared (`null`). Returns the next
 * `{ key, direction }`; `key === null` means "no sort".
 */
export function nextPatientSort(
  current: { readonly key: PatientSortKey | null; readonly direction: SortDirection },
  clicked: PatientSortKey,
): { readonly key: PatientSortKey | null; readonly direction: SortDirection } {
  if (current.key !== clicked) return { key: clicked, direction: 'asc' };
  if (current.direction === 'asc') return { key: clicked, direction: 'desc' };
  return { key: null, direction: 'asc' };
}
